// src\themes\lino\elements\Product\AddToCartButton.tsx
'use client';

import { Loader2, Minus, Plus, ShoppingCart, Trash } from 'lucide-react';
import { toast } from 'sonner';

import type React from 'react';


import { useAuth } from '@/contexts/authContext';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { cn } from '@/core/lib/utils';
import { MainShopProduct } from '@/core/models/shop-product-model';
import Link from "next/link";
import CustomButton from '../../components/CustomButton';

interface AddToCartButtonProps {
    productInfo: MainShopProduct;
    productInventory: number;
    className?: string;
}

export const AddToCartButton: React.FC<AddToCartButtonProps> = ({
    productInfo,
    productInventory,
    className,
}) => {
    const {
        addToCart,
        updateCartItem,
        removeFromCart,
        isCartLoading,
    } = useCartStore();

    const {
        state: { isAuthenticated },
    } = useAuth();

    const { dictionary } = useDictionary();

    const currentCart = useCartStore.getState().cart;
    const currentItem = currentCart?.items?.find(
        //@ts-ignore
        item => item.product_id === productInfo.id
    );
    const currentQuantity = currentItem?.quantity ?? 0;

    const handleAddToCart = async () => {
        if (productInventory <= 0 || isCartLoading) return;

        const availableToAdd = productInventory - currentQuantity;
        if (availableToAdd <= 0) {
            toast.warning(dictionary.ui?.product.noInventory);
            return;
        }

        try {
            await addToCart(productInfo, 1, isAuthenticated);
        } catch (error) {
            console.error('Failed to add to cart:', error);
        }
    };

    const handleIncrease = async () => {
        if (currentQuantity >= productInventory || isCartLoading) return;

        try {
            await updateCartItem(
                //@ts-ignore
                productInfo.id,
                currentQuantity + 1,
                isAuthenticated
            );
        } catch (error) {
            console.error('Failed to increase item quantity:', error);
        }
    };

    const handleDecreaseOrRemove = async () => {
        try {
            if (currentQuantity <= 1) {
                //@ts-ignore
                await removeFromCart(productInfo.id, isAuthenticated);
            } else {
                await updateCartItem(
                    //@ts-ignore
                    productInfo.id,
                    currentQuantity - 1,
                    isAuthenticated
                );
            }
        } catch (error) {
            console.error('Failed to decrease/remove item:', error);
        }
    };

    if (productInventory <= 0) {
        return (
            <div
                className={cn(
                    'inline-flex items-center justify-center px-4 py-3 rounded-2xl bg-red-50 text-red-700 text-[13px] border border-red-100',
                    className
                )}
            >
                {dictionary.ui?.product.noInventory}
            </div>
        );
    }

    return (
        <div className={cn('flex flex-col gap-3', className)} dir="rtl">
            {currentQuantity > 0 ? (<>
                <div className="flex items-center justify-center gap-4 rounded-2xl border border-primary-900 p-1.5">

                    <button
                        onClick={handleDecreaseOrRemove}
                        disabled={isCartLoading}
                        className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-primary-100 text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {currentQuantity <= 1 ? <Trash size={15} /> : <Minus size={15} />}
                    </button>

                    <span className="text-[13px] font-medium min-w-[2rem] text-center text-primary-900">
                        {currentQuantity}
                    </span>

                    <button
                        onClick={handleIncrease}
                        disabled={isCartLoading || currentQuantity >= productInventory}
                        className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-primary-100 text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Plus size={15} />
                    </button>
                </div>
                <div className="flex items-center justify-center gap-2 rounded-2xl border border-primary-300 p-3 hover:border-primary-900 transition-colors">
                    <Link
                        href="/cart"
                        className="flex w-full items-center justify-center gap-2 text-[11px] uppercase tracking-[0.12em] text-primary-900"
                    >
                        {dictionary.ui?.product.goToCart}
                        <ShoppingCart size={16} className="stroke-[1.5]" />
                    </Link>
                </div>
            </>
            ) : (
                <CustomButton
                    onClick={handleAddToCart}
                    disabled={isCartLoading}
                    size="full"
                    className="flex items-center justify-center gap-2"
                >
                    {isCartLoading ? <Loader2 className='animate-spin size-4' /> : <>
                        <Plus size={16} />
                        {dictionary.common.add}
                    </>}
                </CustomButton>
            )}
        </div>
    );
};